import { useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { auth } from '../../firebase';
import { sendEmailVerification } from 'firebase/auth';
import { IconShieldCheck, IconMail, IconCheck } from '@tabler/icons-react';

export default function EmailVerification() {
  const [resent, setResent] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const { orgName } = location.state || {};

  const handleResend = async () => {
    try {
      if (auth.currentUser) {
        await sendEmailVerification(auth.currentUser);
        setResent(true);
        setTimeout(() => setResent(false), 5000);
      }
    } catch (err) {
      console.error(err);
    }
  };

  const steps = [
    { label: 'Account created', done: true },
    { label: 'Verify email', active: true },
    { label: 'Organisation setup', done: false },
    { label: 'Add branches', done: false },
    { label: 'Invite team', done: false },
    { label: 'Connect Outlook', done: false },
    { label: 'Choose plan', done: false },
  ];

  return (
    <div style={{
      display: 'flex',
      height: '100vh',
      width: '100vw',
      fontFamily: 'inherit',
      overflow: 'hidden',
    }}>

      {/* Left panel */}
      <div style={{
        width: '40%',
        background: 'var(--color-brand)',
        padding: '48px 52px',
        display: 'flex',
        flexDirection: 'column',
        flexShrink: 0,
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 48 }}>
          <div style={{
            width: 44,
            height: 44,
            borderRadius: 'var(--radius-md)',
            background: 'rgba(255,255,255,0.2)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}>
            <IconShieldCheck size={22} color="white" stroke={1.5} />
          </div>
          <div>
            <div style={{ fontSize: 20, fontWeight: 400, color: '#fff' }}>Veritas</div>
            <div style={{ fontSize: 11, color: 'rgba(255,255,255,0.6)', marginTop: 2 }}>
              Fleet &amp; equipment compliance
            </div>
          </div>
        </div>

        {/* Progress steps */}
        <div style={{ flex: 1 }}>
          <div style={{ fontSize: 11, color: 'rgba(255,255,255,0.5)', letterSpacing: '0.05em', marginBottom: 16 }}>
            GETTING STARTED
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 0 }}>
            {steps.map((step, i) => (
              <div key={i}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10, paddingBottom: 16 }}>
                  <div style={{
                    width: 22,
                    height: 22,
                    borderRadius: '50%',
                    background: step.done
                      ? '#1D9E75'
                      : step.active
                        ? '#fff'
                        : 'rgba(255,255,255,0.15)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: 10,
                    color: step.active ? 'var(--color-brand)' : 'rgba(255,255,255,0.5)',
                    flexShrink: 0,
                  }}>
                    {step.done
                      ? <IconCheck size={12} stroke={2} color="white" />
                      : i + 1
                    }
                  </div>
                  <span style={{
                    fontSize: 13,
                    color: step.done
                      ? 'rgba(255,255,255,0.6)'
                      : step.active
                        ? '#fff'
                        : 'rgba(255,255,255,0.4)',
                    fontWeight: step.active ? 400 : 400,
                  }}>
                    {step.label}
                  </span>
                </div>
                {i < steps.length - 1 && (
                  <div style={{ width: 1, height: 8, background: 'rgba(255,255,255,0.2)', marginLeft: 10, marginBottom: 0, marginTop: -12 }} />
                )}
              </div>
            ))}
          </div>
        </div>

        <div style={{ fontSize: 10, color: 'rgba(255,255,255,0.35)' }}>
          An <span style={{ color: 'rgba(255,255,255,0.45)', letterSpacing: '0.04em' }}>AGRIWARE</span> product &copy; 2026
        </div>
      </div>

      {/* Right panel */}
      <div style={{
        flex: 1,
        background: 'var(--color-bg-primary)',
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        alignItems: 'center',
        padding: '40px 80px',
      }}>
        <div style={{ width: '100%', maxWidth: 480, textAlign: 'center' }}>

          <div style={{
            width: 72,
            height: 72,
            borderRadius: '50%',
            background: 'var(--color-brand-light)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            margin: '0 auto 24px',
          }}>
            <IconMail size={32} color="var(--color-brand)" stroke={1.5} />
          </div>

          <h1 style={{ fontSize: 26, fontWeight: 400, marginBottom: 8 }}>Check your inbox</h1>
          <p style={{ fontSize: 14, color: 'var(--color-text-secondary)', lineHeight: 1.6, marginBottom: 32, maxWidth: 360, margin: '0 auto 32px' }}>
            We've sent a verification link to{' '}
            <strong style={{ color: 'var(--color-text-primary)' }}>
              {auth.currentUser?.email || 'your email address'}
            </strong>
            . Click the link to continue setting up {orgName || 'your account'}.
          </p>

          {/* Steps */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10, marginBottom: 32, textAlign: 'left' }}>
            {[
              { num: 1, text: <>Open the email from <strong>noreply@veritas.agriware.uk</strong></> },
              { num: 2, text: <>Click <strong>Verify my email</strong> in the message</> },
              { num: 3, text: 'You\'ll be taken straight into your organisation setup' },
            ].map(step => (
              <div key={step.num} style={{
                display: 'flex',
                alignItems: 'center',
                gap: 12,
                background: 'var(--color-bg-secondary)',
                borderRadius: 'var(--radius-md)',
                padding: '12px 14px',
              }}>
                <div style={{
                  width: 24,
                  height: 24,
                  borderRadius: '50%',
                  background: 'var(--color-brand)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: 11,
                  color: '#fff',
                  flexShrink: 0,
                }}>
                  {step.num}
                </div>
                <span style={{ fontSize: 13, color: 'var(--color-text-secondary)' }}>
                  {step.text}
                </span>
              </div>
            ))}
          </div>

          <div
            onClick={handleResend}
            style={{ fontSize: 13, color: 'var(--color-brand)', cursor: 'pointer', marginBottom: 8 }}
          >
            {resent ? '✓ Resent successfully' : 'Resend verification email'}
          </div>
          <div style={{ fontSize: 12, color: 'var(--color-text-tertiary)' }}>
            Didn't receive it? Check your spam folder or resend above.
          </div>

        </div>
      </div>
    </div>
  );
}